import { SessionType } from '../api/generated';
import type {
  InterventionDto,
  InterventionGroupDto,
  MandatoryStaffCompetenceRequirement,
  YearPlanResponse
} from '../api/generated';
import type { CapacityTier } from '../types/weekOverview';
import { CAPACITY_CEILING, CAPACITY_WATCH_THRESHOLD } from '../constants/weekOverviewConstants';

// Classifies a person's day utilization into a capacity tier. Absent people are
// their own tier; otherwise the break-protected ceiling and the watch threshold
// define the boundaries (see the threshold comment in weekOverviewConstants for
// what each tier means).
export function getCapacityTier(utilizationPercent: number, isAbsent: boolean): CapacityTier {
  if (isAbsent) return 'absent';
  if (utilizationPercent >= CAPACITY_CEILING) return 'critical';
  if (utilizationPercent >= CAPACITY_WATCH_THRESHOLD) return 'watch';
  return 'healthy';
}

const MINUTES_PER_HOUR = 60;
const HOURS_DECIMALS = 1;

const toRoundedHours = (minutes: number): number => {
  const factor = 10 ** HOURS_DECIMALS;
  return Math.round((minutes / MINUTES_PER_HOUR) * factor) / factor;
};

// Mirrors the backend's session fan-out (SchedulingInputMapper#expandGroup):
// INDIVIDUAL interventions run as one session per assigned patient (shadow
// patients included — the algorithm schedules their sessions too), while GROUP
// and INTERNAL run as a single session. A missing sessionType is treated as a
// single session.
const countSessions = (intervention: InterventionDto, group: InterventionGroupDto): number =>
  intervention.sessionType === SessionType.Individual
    ? (group.assignedPatients ?? []).length
    : 1;

type PlacedIntervention = {
  intervention: InterventionDto;
  group: InterventionGroupDto;
};

// Flattens the year plan to the interventions placed on the given week,
// keeping each intervention paired with its group (needed for session fan-out).
const collectWeekInterventions = (
  yearPlan: YearPlanResponse,
  weekNumber: number
): PlacedIntervention[] =>
  yearPlan.treatmentPlans.flatMap((plan) =>
    plan.weeks
      .filter((week) => week.weekNumber === weekNumber)
      .flatMap((week) => week.groups)
      .flatMap((group) => group.interventions.map((intervention) => ({ intervention, group })))
  );

// The fields staff demand is read from, shared by a placed intervention and
// the template it was cloned from.
export type StaffDemandIntervention = Pick<
  InterventionDto,
  'durationMinutes' | 'sessionType' | 'mandatoryStaffCompetenceRequirements'
>;

// Per session, a requirement books `count` staff for the intervention's
// duration plus the requirement's pre/post processing time.
const getRequirementStaffMinutes = (
  intervention: StaffDemandIntervention,
  requirement: MandatoryStaffCompetenceRequirement,
  sessions: number
): number => {
  const minutesPerStaff =
    (intervention.durationMinutes ?? 0) +
    (requirement.preProcessingMinutes ?? 0) +
    (requirement.postProcessingMinutes ?? 0);
  return minutesPerStaff * (requirement.count ?? 0) * sessions;
};

const addRequirementMinutes = (
  minutesByProfession: Map<string, number>,
  intervention: StaffDemandIntervention,
  sessions: number
): void => {
  if (sessions === 0) return;
  for (const requirement of intervention.mandatoryStaffCompetenceRequirements ?? []) {
    const staffMinutes = getRequirementStaffMinutes(intervention, requirement, sessions);
    if (staffMinutes === 0) continue;
    for (const professionId of requirement.staffProfessionIds ?? []) {
      minutesByProfession.set(
        professionId,
        (minutesByProfession.get(professionId) ?? 0) + staffMinutes
      );
    }
  }
};

const addInterventionMinutes = (
  minutesByProfession: Map<string, number>,
  { intervention, group }: PlacedIntervention
): void => {
  addRequirementMinutes(minutesByProfession, intervention, countSessions(intervention, group));
};

// Staff minutes per profession that a group's interventions require, on the
// same terms as calculateUsedCompetenceHours, except that INDIVIDUAL sessions
// are counted from the given patient count. A group that has not been saved
// yet has no assigned (or shadow) patients to fan out over, so a planner
// looking ahead counts the seats it will hold instead.
export function calculateInterventionsStaffMinutes(
  interventions: readonly StaffDemandIntervention[],
  patientCount: number
): Map<string, number> {
  const minutesByProfession = new Map<string, number>();
  for (const intervention of interventions) {
    const sessions = intervention.sessionType === SessionType.Individual ? patientCount : 1;
    addRequirementMinutes(minutesByProfession, intervention, sessions);
  }
  return minutesByProfession;
}

// Sums the staff time (in hours, keyed by staffProfessionId) that the
// interventions placed on the given year plan week require. Per session, each
// mandatory requirement books `count` staff for the intervention's duration
// plus the requirement's pre/post processing time. A requirement listing
// several professions books staff holding all of them, so its time counts
// against each listed competence pool. Flex requirements only qualify staff
// the mandatory rows already book — counting them would double-book the same
// person's time — so they add no hours.
export function calculateUsedCompetenceHours(
  yearPlan: YearPlanResponse,
  weekNumber: number
): ReadonlyMap<string, number> {
  const minutesByProfession = new Map<string, number>();
  for (const placed of collectWeekInterventions(yearPlan, weekNumber)) {
    addInterventionMinutes(minutesByProfession, placed);
  }
  return new Map(
    [...minutesByProfession].map(([professionId, minutes]) => [
      professionId,
      toRoundedHours(minutes)
    ])
  );
}
